import React, { useState } from "react";
import {
    Box, Button, Chip, CircularProgress, IconButton, LinearProgress, TextField, Tooltip, Typography,
} from "@mui/material";
// eslint-disable-next-line no-unused-vars -- BsBoxArrowUpRight/BsDownload/BsKey belong to the hidden export buttons
import { BsArrowClockwise, BsBoxArrowUpRight, BsClipboard, BsDownload, BsEnvelope, BsKey, BsStopFill } from "react-icons/bs";
import { addExportRecipientsApi, cancelExportApi, getExportKeyApi, getExportLinkApi } from "./api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 10;

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";

const STATUS = {
    queued: { label: "Queued", color: "#757575" },
    running: { label: "Running", color: "#0F4F96" },
    cancelling: { label: "Stopping", color: "#ed6c02" },
    cancelled: { label: "Stopped", color: "#ed6c02" },
    completed: { label: "Completed", color: "#2e7d32" },
    failed: { label: "Failed", color: "#d32f2f" },
};

const ACTIVE = ["queued", "running", "cancelling"];

// What the job is doing right now. Reading reports row counts; encrypting and
// uploading report nothing, and on a multi-GB export that silence lasts long
// enough to look like the job has hung.
const STAGE_LABEL = {
    reading: "Reading rows",
    encrypting: "Encrypting the file",
    uploading: "Uploading the file",
    emailing: "Sending the email",
};

const formatBytes = (bytes) => {
    const size = Number(bytes) || 0;
    if (!size) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export default function ExportsList({ items, loading, onRefresh, onChanged, onNotify }) {
    // Per-export UI state: the key once revealed, and which button is busy.
    const [keys, setKeys] = useState({});
    const [busy, setBusy] = useState({});

    // Add email id form, per export: { [id]: { value, error } } while open.
    const [emailForms, setEmailForms] = useState({});

    const setBusyFor = (id, value) => setBusy((prev) => ({ ...prev, [id]: value }));

    const setEmailForm = (id, next) => setEmailForms((prev) => {
        const copyOf = { ...prev };
        if (next === null) delete copyOf[id];
        else copyOf[id] = { ...(prev[id] || { value: "", error: "" }), ...next };
        return copyOf;
    });

    // Sends the existing file's link and key to the new addresses only; the
    // query is not run again.
    const handleAddEmails = async (item) => {
        const emails = [...new Set(
            String(emailForms[item.id]?.value || "").split(",").map((e) => e.trim().toLowerCase()).filter(Boolean)
        )];
        if (!emails.length) return setEmailForm(item.id, { error: "Enter at least one email id." });
        if (emails.length > MAX_RECIPIENTS) return setEmailForm(item.id, { error: `At most ${MAX_RECIPIENTS} email ids.` });
        const invalid = emails.find((email) => !EMAIL_RE.test(email));
        if (invalid) return setEmailForm(item.id, { error: `"${invalid}" is not a valid email id.` });

        setBusyFor(item.id, "email");
        try {
            const data = await addExportRecipientsApi(item.id, emails);
            if (data?.statusCode === 200) {
                onNotify("success", data.message || "Email sent");
                setEmailForm(item.id, null);
                onChanged();
            } else {
                setEmailForm(item.id, { error: data?.message || "Could not send the email." });
            }
        } catch (err) {
            setEmailForm(item.id, { error: err?.response?.data?.message || "Could not send the email." });
        } finally {
            setBusyFor(item.id, null);
        }
    };

    /**
     * `which` picks what the button opens:
     *   "console" - the file's page in Google Cloud, the same link recipients
     *               get by email; needs a Google account with access.
     *   "signed"  - a direct download, for the person who ran the export.
     */
    // eslint-disable-next-line no-unused-vars -- its buttons are hidden for now
    const handleOpen = async (item, which) => {
        setBusyFor(item.id, which);
        try {
            const data = await getExportLinkApi(item.id);
            const url = which === "console" ? data.result?.consoleUrl : data.result?.signedUrl;
            if (data?.statusCode === 200 && url) {
                window.open(url, "_blank", "noopener,noreferrer");
            } else {
                onNotify("failed", data?.message || "Could not open the file.");
            }
        } catch (err) {
            onNotify("failed", "Could not open the file.");
        } finally {
            setBusyFor(item.id, null);
        }
    };

    // eslint-disable-next-line no-unused-vars -- its button is hidden for now
    const handleShowKey = async (item) => {
        setBusyFor(item.id, "key");
        try {
            const data = await getExportKeyApi(item.id);
            if (data?.statusCode === 200 && data.result?.key) {
                setKeys((prev) => ({ ...prev, [item.id]: data.result.key }));
            } else {
                onNotify("failed", data?.message || "Could not read the key.");
            }
        } catch (err) {
            onNotify("failed", "Could not read the key.");
        } finally {
            setBusyFor(item.id, null);
        }
    };

    const handleStop = async (item) => {
        setBusyFor(item.id, "stop");
        try {
            const data = await cancelExportApi(item.id);
            onNotify(data?.statusCode === 200 ? "success" : "failed", data?.message || "");
            onChanged();
        } catch (err) {
            onNotify("failed", "Could not stop the export.");
        } finally {
            setBusyFor(item.id, null);
        }
    };

    const copy = (text) => {
        navigator.clipboard?.writeText(text)
            .then(() => onNotify("success", "Key copied"))
            .catch(() => onNotify("failed", "Could not copy"));
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    Your exports · delivered as files, not shown on screen
                </Typography>
                <Tooltip title="Reload">
                    <span>
                        <IconButton size="small" onClick={onRefresh} disabled={loading}>
                            <BsArrowClockwise size={16} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                {loading && items.length === 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {!loading && items.length === 0 && (
                    <Typography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>
                        No exports yet. Write a query and choose <b>Export data</b>.
                    </Typography>
                )}

                {items.map((item) => {
                    const status = STATUS[item.status] || { label: item.status, color: "#757575" };
                    const isActive = ACTIVE.includes(item.status);
                    const revealedKey = keys[item.id];
                    return (
                        <Box key={item.id} sx={{ borderBottom: "1px solid #eee", px: 1.5, py: 1.25 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>#{item.id}</Typography>
                                <Chip
                                    size="small"
                                    label={status.label}
                                    sx={{ height: 20, fontSize: 11, color: status.color, borderColor: status.color }}
                                    variant="outlined"
                                />
                                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                                    {item.queuedat} IST
                                </Typography>
                            </Box>

                            {item.exportname && (
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.25 }}>
                                    {item.exportname}
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    fontSize: 12, fontFamily: MONO, whiteSpace: "pre-wrap", wordBreak: "break-word",
                                    color: item.exportname ? "text.secondary" : "text.primary",
                                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                                    mb: 0.5,
                                }}
                                title={item.querytext}
                            >
                                {item.querytext}
                            </Typography>

                            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                                {Number(item.rowcount || 0).toLocaleString()} rows
                                {item.filesizebytes ? ` · ${formatBytes(item.filesizebytes)}` : ""}
                                {item.recipients?.length ? ` · emailed to ${item.recipients.join(", ")}` : " · no email"}
                            </Typography>

                            {isActive && (
                                <>
                                    <Typography sx={{ fontSize: 11, color: "#0F4F96", mt: 0.5 }}>
                                        {STAGE_LABEL[item.stage] || "Working"}
                                        {item.stage === "uploading" && item.filesizebytes
                                            ? ` · ${formatBytes(item.filesizebytes)}`
                                            : ""}
                                        …
                                    </Typography>
                                    <LinearProgress sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
                                </>
                            )}

                            {/* A search on an encrypted column runs against both the
                                plain and the encrypted form, so say so - the SQL that
                                ran is not quite the SQL that was typed. */}
                            {item.rewrites?.length > 0 && (
                                <Typography sx={{ fontSize: 11, color: "#0F4F96", mt: 0.5 }}>
                                    {item.rewrites.map((r) => r.column).join(", ")} searched using the
                                    encrypted form as well
                                </Typography>
                            )}

                            {item.errormessage && (
                                <Typography sx={{ fontSize: 11, color: "error.main", mt: 0.5, whiteSpace: "pre-wrap" }}>
                                    {item.errormessage}
                                </Typography>
                            )}

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                {item.status === "completed" && (
                                    <>
                                        {/* Open in GCP, Download and Show key are hidden for now - only Add email id is offered.
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            title="Opens the file in Google Cloud - the same link recipients receive"
                                            startIcon={busy[item.id] === "console" ? <CircularProgress size={12} color="inherit" /> : <BsBoxArrowUpRight />}
                                            onClick={() => handleOpen(item, "console")}
                                            disabled={Boolean(busy[item.id])}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Open in GCP
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            title="Direct download, no Google account needed"
                                            startIcon={busy[item.id] === "signed" ? <CircularProgress size={12} color="inherit" /> : <BsDownload />}
                                            onClick={() => handleOpen(item, "signed")}
                                            disabled={Boolean(busy[item.id])}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Download
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={busy[item.id] === "key" ? <CircularProgress size={12} color="inherit" /> : <BsKey />}
                                            onClick={() => handleShowKey(item)}
                                            disabled={Boolean(busy[item.id]) || Boolean(revealedKey)}
                                            sx={{ textTransform: "none" }}
                                        >
                                            {revealedKey ? "Key shown" : "Show key"}
                                        </Button>
                                        */}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            title="Email this file's link and key to more people - the query is not run again"
                                            startIcon={<BsEnvelope />}
                                            onClick={() => setEmailForm(item.id, {})}
                                            disabled={Boolean(busy[item.id]) || Boolean(emailForms[item.id])}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Add email id
                                        </Button>
                                    </>
                                )}
                                {isActive && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={busy[item.id] === "stop" ? <CircularProgress size={12} color="inherit" /> : <BsStopFill />}
                                        onClick={() => handleStop(item)}
                                        disabled={Boolean(busy[item.id])}
                                        sx={{ textTransform: "none" }}
                                    >
                                        Stop
                                    </Button>
                                )}
                            </Box>

                            {emailForms[item.id] && (
                                <Box sx={{ mt: 1, p: 1, border: "1px solid #ddd", borderRadius: 1 }}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        size="small"
                                        label="Email ids"
                                        placeholder="name1@example.com, name2@example.com"
                                        value={emailForms[item.id].value}
                                        onChange={(e) => setEmailForm(item.id, { value: e.target.value, error: "" })}
                                        onKeyDown={(e) => { if (e.key === "Enter") handleAddEmails(item); }}
                                        error={Boolean(emailForms[item.id].error)}
                                        helperText={emailForms[item.id].error || "Comma separated. Gets the same link and key; the query is not run again."}
                                        disabled={busy[item.id] === "email"}
                                    />
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setEmailForm(item.id, null)}
                                            disabled={busy[item.id] === "email"}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => handleAddEmails(item)}
                                            disabled={busy[item.id] === "email"}
                                            startIcon={busy[item.id] === "email" ? <CircularProgress size={12} color="inherit" /> : null}
                                            sx={{ textTransform: "none" }}
                                        >
                                            {busy[item.id] === "email" ? "Sending..." : "Send"}
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {revealedKey && (
                                <Box sx={{ mt: 1, p: 1, backgroundColor: "#f4f6f8", border: "1px solid #ddd", borderRadius: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                        <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                                            Secret key · paste into the Candidate Dump Decryptor
                                        </Typography>
                                        <Tooltip title="Copy key">
                                            <IconButton size="small" onClick={() => copy(revealedKey)}>
                                                <BsClipboard size={12} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Typography sx={{ fontSize: 11, fontFamily: MONO, wordBreak: "break-all" }}>
                                        {revealedKey}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
